import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceArea, Tooltip } from 'recharts';
import { Heart, Wind, Activity, Droplets, Thermometer, TriangleAlert, Download, Printer, Calendar, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { database } from '../../../firebase';
import { ref, onValue, off } from 'firebase/database';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Alerts from './alerts';
import constant from '../../../constant';
import VitalBoxes from '../ui/vitalBoxes';   // <-- NEW

const PatientEcgDashboard = () => {
    const { hospId, patientId, bedId } = useParams();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [patientInfo, setPatientInfo] = useState(null);
    const [patient, setPatient] = useState([]);
    const [ecgData, setEcgData] = useState([]);
    const [vitals, setVitals] = useState({
        ews: 0, hr: 0, resp_rate: 0,
        nibp_systolic: 0, nibp_diastolic: 0,
        spo2: 0, temp: 0,
    });

    useEffect(() => {
        // Fetch patient data
        const fetchPatientData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem('token');
                if (!token || !hospId) {
                    alert('No authentication token or hospital ID found');
                    return;
                }

                const patientUrl = `${constant.REACT_APP_API_URL}/hospital/${hospId}/patient/${patientId}`;
                const patientResponse = await fetch(patientUrl, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });

                if (!patientResponse.ok) {
                    const message = await patientResponse.text();
                    throw new Error(message || 'Failed to fetch patient data');
                }

                const patientResult = await patientResponse.json();
                setPatient(patientResult);
            } catch (error) {
                setError(error.message || 'An error occurred while fetching patient data');
            } finally {
                setIsLoading(false);
            }
        };

        // Real-time ECG: 5-second sliding window
        const ecgRef = ref(database, `hospital/${hospId}/${bedId}/ecg`);

        const unsubscribe = onValue(ecgRef, (snapshot) => {
            const data = snapshot.val();
            if (!data) {
                setEcgData([]);
                return;
            }

            // Convert to array with timestamp
            const rawDataPoints = Object.keys(data).map((key) => ({
                timestamp: data[key].time * 1000, // Firebase timestamp in seconds → ms
                value: data[key].value[0],
            }));

            // Sort by time
            rawDataPoints.sort((a, b) => a.timestamp - b.timestamp);

            const now = Date.now();
            const fiveSecondsAgo = now - 6000;

            // Keep only last 5 seconds
            const recentPoints = rawDataPoints.filter(
                (point) => point.timestamp >= fiveSecondsAgo
            );

            // Use first point as reference (0s)
            const firstTimestamp = recentPoints.length > 0 ? recentPoints[0].timestamp : now;

            // Format for chart: relative time in seconds (0.00 to 5.00)
            const formattedData = recentPoints.map((point) => ({
                time: ((point.timestamp - firstTimestamp) / 1000).toFixed(0), // e.g., "0.00", "1.23"
                value: point.value,
            }));

            setEcgData(formattedData);
        }, (error) => {
            setError(error.message);
        });

        fetchPatientData();

        // Cleanup
        return () => {
            off(ecgRef);
        };
    }, [hospId, patientId, bedId]);


    useEffect(() => {
        if (!hospId || !bedId) return;

        const refs = {};
        const today = Math.floor(Date.now() / 1000);
        const startOfDay = today - (today % 86400); // UTC midnight

        const vitalTypes = ['ews', 'hr', 'resp_rate', 'nibp', 'spo2', 'temp'];

        vitalTypes.forEach(type => {
            const r = ref(database, `hospital/${hospId}/${bedId}/${type}`);
            refs[type] = r;

            onValue(r, snap => {
                const data = snap.val();
                if (!data) return;

                const entries = Object.entries(data)
                    .map(([k, v]) => {
                        if (type === 'nibp') {
                            const [s, d] = (v.value + '').split('/').map(Number);
                            return { ts: v.time, sys: s, dia: d };
                        }
                        return { ts: v.time, val: v.value };
                    })
                    .filter(o => o.ts >= startOfDay)
                    .sort((a, b) => a.ts - b.ts);

                const latest = entries[entries.length - 1];
                if (!latest) return;

                setVitals(prev => ({
                    ...prev,
                    ...(type === 'nibp'
                        ? { nibp_systolic: latest.sys ?? 0, nibp_diastolic: latest.dia ?? 0 }
                        : { [type]: latest.val ?? 0 }),
                }));
            });
        });

        return () => Object.values(refs).forEach(off);
    }, [hospId, bedId]);

    const handlePrint = () => {
        window.print();
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading patient data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center text-red-600">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                    <p>Error: {error}</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>
                {`
    @media print {
        * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
        }
        body * {
            visibility: hidden;
        }
        .printable-content,
        .printable-content * {
            visibility: visible;
        }
        .printable-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 1cm;
            font-size: 12pt;
            background: white !important;
        }
        .no-print {
            display: none !important;
        }
        .flex-col,
        .lg\\:flex-row {
            display: flex !important;
            flex-direction: row !important;
        }
        .lg\\:basis-10\\/12 {
            flex: 5 !important;
        }
        .lg\\:basis-2\\/12 {
            flex: 1 !important;
        }
        .h-32 {
            height: 160px !important;
        }
        .text-3xl {
            font-size: 18pt !important;
        }
        .text-base, .text-sm, .text-xs {
            font-size: 10pt !important;
        }
        .rounded-lg {
            border-radius: 8px !important;
        }
        .shadow-lg {
            box-shadow: none !important;
        }
        .bg-gray-100,
        .bg-green-50,
        .bg-yellow-50,
        .bg-red-50,
        .bg-blue-500,
        .bg-blue-600,
        .bg-green-400,
        .bg-yellow-400,
        .bg-red-400 {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
        }
        .text-green-600,
        .text-yellow-600,
        .text-red-600,
        .text-gray-600 {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
        }
        .mb-6 {
            margin-bottom: 1cm !important;
        }
        .p-6 {
            padding: 1cm !important;
        }
        .h-5, .w-5, .h-4, .w-4 {
            width: 16pt !important;
            height: 16pt !important;
        }
    }
`}
            </style>

            <div className="printable-content bg-white rounded-lg shadow-lg p-6 flex flex-col lg:flex-row gap-6">


                <div className="flex-1 lg:basis-10/12">
                    <div className="mb-6 bg-gray-100 p-4 rounded-lg flex-col lg:flex-row lg:items-center lg:justify-between">
                        <div className="items-center space-x-4">
                            <div className="grid grid-cols-12 gap-4 items-center">
                                <div className="col-span-2 flex items-center">
                                    <button
                                        onClick={() => navigate(-1)}
                                        className="text-sm brand-border flex justify-center items-center py-2 text-brand-blue hover:brand-white hover:brand-blue transition-all duration-300 px-3 no-print"
                                    >
                                        <ChevronLeft className="w-4 h-4 mr-2" />
                                        All Patients
                                    </button>
                                </div>
                                <div className="col-span-4 text-sm">
                                    <p className="text-gray-600 font-medium">
                                        Patient Name: <span className='capitalize'>{patient.name || "N/A"}</span>
                                    </p>
                                    <p className="text-gray-600 font-medium">
                                        Patient ID: <span>{patient.patient_hospital_id || "N/A"}</span>
                                    </p>
                                </div>
                                <div className="col-span-4 text-sm">
                                    <p className="text-gray-600 font-medium">
                                        Bed #: <span>{patient.bed_number || "N/A"}</span>
                                    </p>
                                    <p className="text-gray-600 font-medium">
                                        Admitted On: <span>
                                            {patient.admitted_on
                                                ? new Date(patient.admitted_on).toLocaleDateString("en-US", {
                                                    year: "numeric",
                                                    month: "long",
                                                    day: "numeric",
                                                })
                                                : "N/A"}
                                        </span>
                                    </p>
                                    <p className="text-gray-600 font-medium">
                                        Consulting Doctor: <span className='capitalize'>{patient.doctor?.name || 'N/A'}</span>
                                    </p>
                                </div>
                                <div className="col-span-2 flex items-center justify-end">
                                    <button
                                        onClick={handlePrint}
                                        className="brand-border flex justify-center text-sm items-center py-2 text-brand-blue hover:brand-white hover:brand-blue transition-all duration-300 px-3 no-print"
                                    >
                                        <Printer className="mr-2" />
                                        Print Report
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="flex space-x-4 mt-4 lg:mt-0"></div>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">ECG Report</h2>

                    <VitalBoxes vitals={vitals} />

                    <div className="my-8">
                        <ResponsiveContainer width="100%" height={400}>
                            <LineChart data={ecgData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                <XAxis
                                    dataKey="time"
                                    tick={{ fontSize: 12 }}
                                // label={{ value: 'Time', position: 'bottom', offset: 0 }}
                                />
                                <YAxis
                                    // label={{ value: 'Voltage (μV)', angle: -90, position: 'insideLeft' }}
                                    tick={{ fontSize: 12 }}
                                />
                                <Tooltip />
                                <Line
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#ff0000"
                                    dot={false}
                                    strokeWidth={2}
                                    animationDuration={0}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    {/* <div className="relative group flex justify-end no-print">
                        <Link
                            to="#"
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            className="text-sm brand-border flex justify-center items-center py-2 text-brand-blue hover:brand-white hover:brand-blue transition-all duration-300 px-3"
                        >
                            <Download size={18} />
                            <span className="ml-2">Download Report</span>
                        </Link>
                    </div> */}
                </div>
                <div className="flex-1 lg:basis-2/12 no-print">
                    <Alerts hospitalId={hospId} patientId={patientId} />
                </div>
            </div>
        </>
    );
};

export default PatientEcgDashboard;